import { useMemo, useCallback, useRef, useState, useEffect } from "react";

/**
 * Advanced column state manager that pre-configures column definitions
 * to eliminate column rearranging flash completely
 */
const useColumnStateManager = (tableId, originalColumnDefs) => {
  const [savedColumnState, setSavedColumnState] = useState(null);
  const [isLoading, setIsLoading] = useState(!!tableId);

  // Load saved state on mount
  useEffect(() => {
    if (!tableId) {
      setIsLoading(false);
      return;
    }

    const stateKey = `ag-grid-column-state-${tableId}`;
    const saved = localStorage.getItem(stateKey);

    if (saved) {
      try {
        const parsedState = JSON.parse(saved);
        setSavedColumnState(parsedState);
        // console.log(`📋 Loaded saved column state for ${tableId}`);
      } catch (error) {
        console.warn(`❌ Failed to parse saved column state for ${tableId}:`, error);
      }
    }

    setIsLoading(false);
  }, [tableId]);

  // Pre-configure column definitions with saved state
  const configuredColumnDefs = useMemo(() => {
    if (!savedColumnState || !originalColumnDefs || !Array.isArray(originalColumnDefs)) {
      return originalColumnDefs;
    }

    // console.log("🔧 Pre-configuring column definitions with saved state");

    // Create a map of saved column states by colId
    const stateMap = new Map();
    savedColumnState.forEach((state) => {
      stateMap.set(state.colId, state);
    });

    // Apply saved state to column definitions
    const configured = originalColumnDefs.map((colDef) => {
      const colId = colDef.field || colDef.colId;
      const savedState = stateMap.get(colId);

      if (savedState) {
        return {
          ...colDef,
          width: savedState.width || colDef.width,
          hide: savedState.hide || false,
          pinned: savedState.pinned || colDef.pinned,
          sort: savedState.sort || null,
          sortIndex: savedState.sortIndex || null,
        };
      }

      return colDef;
    });

    // Sort columns according to saved order
    const sortedColumns = [...configured];

    // Create ordered array based on saved state
    const orderedColumns = [];
    savedColumnState.forEach((state) => {
      const colDef = sortedColumns.find((def) => (def.field || def.colId) === state.colId);
      if (colDef) {
        orderedColumns.push(colDef);
        // Remove from sorted columns to avoid duplicates
        const index = sortedColumns.indexOf(colDef);
        if (index > -1) {
          sortedColumns.splice(index, 1);
        }
      }
    });

    // Add any remaining columns that weren't in the saved state
    orderedColumns.push(...sortedColumns);

    return orderedColumns;
  }, [originalColumnDefs, savedColumnState]);

  // Save column state
  const saveColumnState = useCallback(
    (columnState) => {
      if (tableId && columnState) {
        try {
          const stateKey = `ag-grid-column-state-${tableId}`;
          localStorage.setItem(stateKey, JSON.stringify(columnState));
          setSavedColumnState(columnState);
          //   console.log(`✅ Column state saved for ${tableId}`);
        } catch (error) {
          console.warn(`❌ Failed to save column state for ${tableId}:`, error);
        }
      }
    },
    [tableId]
  );

  return {
    columnDefs: configuredColumnDefs,
    isLoading,
    savedColumnState,
    saveColumnState,
    hasSavedState: !!savedColumnState,
  };
};

export default useColumnStateManager;
